import { Component, OnInit, OnDestroy } from '@angular/core';

import { Observable, forkJoin, Subject } from 'rxjs';
import { map, tap, shareReplay, takeUntil, filter } from 'rxjs/operators';

import { SvgIconsModule } from '@library/common/materials';
import { Preferences } from '@library/core/services';
import { UseraccessService } from '@library/modules/user';
import { ShipmentService } from '../shipment.service';

import { ShipmentTypes, ShipmentModes, MovementTypes, ShipmentFactory } from '@library/common/models';

const typeMap = {
   domestic: [ 'domestic_booking' ],
   international: [ 'international_booking', 'ocean_booking' ],
};

const modeMap = {
   road: [ 'international_booking' ],
   air: [ 'international_booking' ],
   ocean: [ 'ocean_booking' ],
};

const quoteTypeMap = {
   domestic: [ 'domestic_quoting' ],
   international: [ 'international_quoting', 'ocean_quoting' ],
};

const quoteModeMap = {
   road: [ 'international_quoting' ],
   air: [ 'international_quoting' ],
   ocean: [ 'ocean_quoting' ],
};

@Component({
   selector: 'shipment-type',
   templateUrl: './type.component.html',
   styleUrls: ['./type.component.scss']
})
export class TypeComponent implements OnInit, OnDestroy {

   types: ShipmentTypes[];
   modes: ShipmentModes[];
   movements = MovementTypes;

   _onDestroy: Subject<void> = new Subject();

   types$: Observable<ShipmentTypes[]> = forkJoin(
      ShipmentTypes.map( t => {
         if ( this.shipment.shipmentForm.get('isQuote').value ) {
            return this.access.hasAccess( quoteTypeMap[t] );
         }
         return this.access.hasAccess( typeMap[t] );
      })
   ).pipe(
      map( bools => ShipmentTypes.filter(( _, i ) => bools[i] )),
      tap( types => this.types = types ),
      tap( types => this.prefDefaults.type = types[0] ),
      tap( types => types.map( type => this.svgIcons.register( type ))),
      shareReplay(),
      takeUntil( this._onDestroy ),
   );

   modes$: Observable<ShipmentModes[]> = forkJoin(
      ShipmentModes.map( m => {
         if ( this.shipment.shipmentForm.get('isQuote').value ) {
            return this.access.hasAccess( quoteModeMap[m] );
         }
         return this.access.hasAccess( modeMap[m] );
      })
   ).pipe(
      map( bools => ShipmentModes.filter(( _, i ) => bools[i] )),
      tap( modes => this.modes = modes ),
      tap( modes => this.prefDefaults.mode = modes[0] ),
      tap( modes => modes.map( mode => this.svgIcons.register( mode ))),
      shareReplay(),
      takeUntil( this._onDestroy ),
   );

   prefDefaults: { type: ShipmentTypes, mode: ShipmentModes } = {
      type: ShipmentTypes[0],
      mode: ShipmentModes[0],
   };

   get pref() { return this.prefs.getPref( 'booking' ); }
   get typePref() { return this.pref?.type ?? this.types[0]; }
   get modePref() { return this.pref?.mode ?? this.modes[0]; }

   public disabled_type = false;

   constructor(
      public shipment: ShipmentService,
      public prefs: Preferences,
      private svgIcons: SvgIconsModule,
      private access: UseraccessService,
   ) {}

   ngOnInit() {
      forkJoin( this.types$, this.modes$ ).pipe(
         filter( _ => this.shipment.new ),
         takeUntil( this._onDestroy ),
      ).subscribe(([ types, modes ]) =>
         this.setType(
            types?.[this.pref?.type] ?? types[0],
            modes?.[this.pref?.mode] ?? modes[0]
         ));

      this.shipment.shipmentUpdate$.subscribe((s) => {
         if ( s ) {
            this.disabled_type = this.shipment.shipmentForm.get('shipType').disabled;
         }
      });
   }

   ngOnDestroy() {
      this._onDestroy.next();
      this._onDestroy.complete();
   }

   setType( shipType: ShipmentTypes, mode: ShipmentModes = this.modes[0] ): void {
      if ( !this.types.includes( shipType )) { shipType = this.types[0]; }
      if ( !this.modes.includes( mode )) { mode = this.modes[0]; }

      this.shipment.shipmentForm = ShipmentFactory.formGroup({
         ...this.shipment.shipment,
         shipType,
         mode
      });

      if ( shipType !== this.typePref ) {
         this.prefs.upsertPref( 'booking', { type: shipType });
      }

      if ( mode !== this.modePref ) {
         this.prefs.upsertPref( 'booking', { mode });
      }
   }
}
